/**
 * CommissionConfigPage
 *
 * Manage per-referrer commission rates for the clinic. Any clinic user
 * (practitioner, clinic_admin, etc.) can have a rate.
 */

import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useClinic } from '@/contexts/ClinicContext'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Pencil, Plus, XCircle } from 'lucide-react'
import {
  listConfigs,
  createConfig,
  updateConfig,
  deactivateConfig,
  getReferrers,
  getClinicDefaultRate,
  setClinicDefaultRate,
  CommissionConfig,
  Referrer,
} from '@/services/commissionService'
import ReferringProviderSelect from './ReferringProviderSelect'

const ROLE_LABELS: Record<string, string> = {
  practitioner: 'Practitioner',
  clinic_admin: 'Clinic Admin',
  admin: 'Admin',
  super_admin: 'System Admin',
}

export function CommissionConfigPage() {
  const { clinic, isAdmin } = useClinic()
  const clinicId = clinic?.id
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingConfig, setEditingConfig] = useState<CommissionConfig | null>(
    null,
  )
  const [addForm, setAddForm] = useState<{
    referringUserId: string | null
    commissionRateBps: number | null
    effectiveFrom: string
    effectiveTo: string
  }>({
    referringUserId: null,
    commissionRateBps: 2000,
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: '',
  })
  const [editForm, setEditForm] = useState<{
    ratePercentage: number
    effectiveTo: string
  }>({ ratePercentage: 20, effectiveTo: '' })

  const [showDefaultDialog, setShowDefaultDialog] = useState(false)
  const [defaultRatePct, setDefaultRatePct] = useState<number>(20)

  const { data: defaultRate } = useQuery({
    queryKey: ['commission-default-rate', clinicId],
    queryFn: () => getClinicDefaultRate(clinicId!),
    enabled: !!clinicId,
  })

  const { data: configs, isLoading } = useQuery({
    queryKey: ['commission-configs', clinicId],
    queryFn: () => listConfigs(clinicId!, { includeInactive: true }),
    enabled: !!clinicId,
  })

  const { data: referrers } = useQuery({
    queryKey: ['referrers', clinicId],
    queryFn: () => getReferrers(clinicId!),
    enabled: !!clinicId,
  })

  const referrerByUserId = useMemo(() => {
    const m = new Map<string, Referrer>()
    for (const r of referrers || []) m.set(r.user_id, r)
    return m
  }, [referrers])

  const createMutation = useMutation({
    mutationFn: () =>
      createConfig(clinicId!, {
        referrerUserId: addForm.referringUserId!,
        ratePercentage: (addForm.commissionRateBps || 2000) / 100,
        effectiveFrom: addForm.effectiveFrom,
        effectiveTo: addForm.effectiveTo || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-configs'] })
      setShowAddDialog(false)
      resetAddForm()
      toast({
        title: 'Rate saved',
        description: 'Referral rate has been created.',
      })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not create rate',
        description: error.message,
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({
      configId,
      data,
    }: {
      configId: string
      data: { ratePercentage?: number; effectiveTo?: string }
    }) => updateConfig(clinicId!, configId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-configs'] })
      setEditingConfig(null)
      toast({ title: 'Rate updated' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not update',
        description: error.message,
      })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (configId: string) => deactivateConfig(clinicId!, configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-configs'] })
      toast({ title: 'Rate deactivated' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not deactivate',
        description: error.message,
      })
    },
  })

  const defaultRateMutation = useMutation({
    mutationFn: (ratePercentage: number) =>
      setClinicDefaultRate(clinicId!, ratePercentage),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['commission-default-rate'],
      })
      setShowDefaultDialog(false)
      toast({ title: 'Clinic default rate updated' })
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Could not update default rate',
        description: error.message,
      })
    },
  })

  const openDefaultDialog = () => {
    setDefaultRatePct(defaultRate?.ratePercentage ?? 20)
    setShowDefaultDialog(true)
  }

  const resetAddForm = () => {
    setAddForm({
      referringUserId: null,
      commissionRateBps: 2000,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
    })
  }

  const openEditDialog = (config: CommissionConfig) => {
    setEditingConfig(config)
    setEditForm({
      ratePercentage:
        config.ratePercentage ?? config.commission_rate_bps / 100,
      effectiveTo: config.effective_to || '',
    })
  }

  if (!clinicId) {
    return <div className="p-6">No clinic selected.</div>
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            You need clinic admin access to view referral rates.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Clinic default rate */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Clinic Default Rate</CardTitle>
            <CardDescription>
              Applied to any referrer without a per-user rate below.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={openDefaultDialog}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-semibold">
            {defaultRate
              ? `${defaultRate.ratePercentage.toFixed(1)}%`
              : '—'}
          </div>
        </CardContent>
      </Card>

      {/* Per-user overrides */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Referral Rates</CardTitle>
            <CardDescription>
              Per-user overrides. Anyone not listed earns the clinic default.
            </CardDescription>
          </div>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Rate
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Effective From</TableHead>
                  <TableHead>Effective To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs?.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-muted-foreground"
                    >
                      No per-user overrides. Every referrer earns the clinic
                      default
                      {defaultRate
                        ? ` (${defaultRate.ratePercentage.toFixed(1)}%).`
                        : '.'}
                    </TableCell>
                  </TableRow>
                )}
                {configs?.map(config => {
                  const directoryEntry =
                    referrerByUserId.get(config.referrer_user_id)
                  const name =
                    config.referrer?.name ||
                    directoryEntry?.name ||
                    config.referrer_user_id.slice(0, 8)
                  const role =
                    config.role_snapshot ||
                    config.referrer?.primary_role ||
                    directoryEntry?.primary_role ||
                    ''
                  return (
                    <TableRow key={config.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {ROLE_LABELS[role] || role.replace(/_/g, ' ') || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(
                          config.ratePercentage ??
                          config.commission_rate_bps / 100
                        ).toFixed(1)}
                        %
                      </TableCell>
                      <TableCell>{config.effective_from}</TableCell>
                      <TableCell>
                        {config.effective_to || 'Indefinite'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={config.is_active ? 'default' : 'secondary'}
                        >
                          {config.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(config)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {config.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                deactivateMutation.mutate(config.id)
                              }
                            >
                              <XCircle className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Rate Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Referral Rate</DialogTitle>
            <DialogDescription>
              Pick a clinic user and the commission percentage they should
              receive for guided orders they refer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <ReferringProviderSelect
              clinicId={clinicId}
              value={{
                referringUserId: addForm.referringUserId,
                commissionRateBps: addForm.commissionRateBps,
              }}
              onChange={val =>
                setAddForm({
                  ...addForm,
                  referringUserId: val.referringUserId,
                  commissionRateBps: val.commissionRateBps,
                })
              }
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <Input
                  type="date"
                  value={addForm.effectiveFrom}
                  onChange={e =>
                    setAddForm({ ...addForm, effectiveFrom: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Effective To (Optional)</Label>
                <Input
                  type="date"
                  value={addForm.effectiveTo}
                  onChange={e =>
                    setAddForm({ ...addForm, effectiveTo: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={
                !addForm.referringUserId ||
                !addForm.commissionRateBps ||
                createMutation.isPending
              }
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rate Dialog */}
      <Dialog
        open={!!editingConfig}
        onOpenChange={open => !open && setEditingConfig(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Referral Rate</DialogTitle>
            <DialogDescription>
              Update the rate or when it stops being effective.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Commission Rate (%)</Label>
              <Input
                type="number"
                min={1}
                max={50}
                step={0.5}
                value={editForm.ratePercentage}
                onChange={e =>
                  setEditForm({
                    ...editForm,
                    ratePercentage: parseFloat(e.target.value) || 20,
                  })
                }
              />
              {(editForm.ratePercentage < 1 || editForm.ratePercentage > 50) && (
                <p className="text-xs text-destructive">
                  Commission rate must be between 1% and 50%.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Effective To (Optional)</Label>
              <Input
                type="date"
                value={editForm.effectiveTo}
                onChange={e =>
                  setEditForm({ ...editForm, effectiveTo: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConfig(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                editingConfig &&
                updateMutation.mutate({
                  configId: editingConfig.id,
                  data: {
                    ratePercentage: editForm.ratePercentage,
                    effectiveTo: editForm.effectiveTo || undefined,
                  },
                })
              }
              disabled={
                updateMutation.isPending ||
                editForm.ratePercentage < 1 ||
                editForm.ratePercentage > 50
              }
            >
              {updateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Default Rate Dialog */}
      <Dialog open={showDefaultDialog} onOpenChange={setShowDefaultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Clinic Default Rate</DialogTitle>
            <DialogDescription>
              This rate is used for any referrer without a per-user override.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Commission Rate (%)</Label>
              <Input
                type="number"
                min={1}
                max={50}
                step={0.5}
                value={defaultRatePct}
                onChange={e =>
                  setDefaultRatePct(parseFloat(e.target.value) || 0)
                }
              />
              {(defaultRatePct < 1 || defaultRatePct > 50) ? (
                <p className="text-xs text-destructive">
                  Commission rate must be between 1% and 50%.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Allowed range: 1% – 50%.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDefaultDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => defaultRateMutation.mutate(defaultRatePct)}
              disabled={
                defaultRatePct < 1 ||
                defaultRatePct > 50 ||
                defaultRateMutation.isPending
              }
            >
              {defaultRateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CommissionConfigPage
